/**
 * ReportHubPage — /reports/category/:slug and /reports/organisation/:slug.
 * Public hub with an intro, the paginated list (?page=N) and links to the
 * other hubs (3/9 SEO expansion). Organisation hubs exist only for ≥3 reports.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, RefreshCw, ExternalLink, FileText } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";
import ReportCard from "../components/ReportCard";
import ReportsSignupCta from "../components/reports/ReportsSignupCta";
import { API } from "../config";
import { setCanonical, setDescription, setJsonLd, setNoIndex, SITE_URL } from "../lib/seo";

const PAGE_SIZE = 24;

export default function ReportHubPage({ kind }) {
  const { slug } = useParams();
  const location = useLocation();
  const page = useMemo(() => {
    const p = parseInt(new URLSearchParams(location.search).get("page"), 10);
    return Number.isFinite(p) && p > 1 ? p : 1;
  }, [location.search]);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [gone, setGone] = useState(false);
  const [dm] = useDarkMode();
  const base = `/reports/${kind}/${slug}`;

  useEffect(() => {
    setData(null); setNotFound(false); setGone(false);
    axios.get(`${API}${base}`, { params: { page, limit: PAGE_SIZE } })
      .then(({ data: d }) => setData(d)).catch((e) => { setNotFound(true); setGone(e?.response?.status === 404); });
  }, [base, page]);

  useEffect(() => (gone ? setNoIndex() : undefined), [gone]);

  const head = data ? (kind === "category" ? data.category : data.organisation) : null;
  const name = head ? (kind === "category" ? head.label : head.organisation) : "";

  useEffect(() => {
    if (!data) return undefined;
    const title = kind === "category"
      ? `${name} — travel industry reports & research | TourismIntel`
      : `${name} reports & research — briefed by TourismIntel`;
    document.title = page > 1 ? `${title} (page ${page})` : title;
    const desc = kind === "category"
      ? (head.intro || "").split("\n")[0].slice(0, 300)
      : `All ${data.total} reports published by ${name} in the TourismIntel library, each with an original brief: key findings, numbers, methodology and the official link.`;
    const c1 = setDescription(desc);
    setCanonical(`${base}${page > 1 ? `?page=${page}` : ""}`);
    const c2 = setJsonLd("report-hub-jsonld", {
      "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${SITE_URL}${base}`, name: title, description: desc, inLanguage: "en",
      mainEntity: { "@type": "ItemList", numberOfItems: data.total, itemListElement: (data.reports || []).map((r, i) => ({
        "@type": "ListItem", position: (page - 1) * PAGE_SIZE + i + 1, url: `${SITE_URL}/reports/${r.slug}`, name: r.clean_title || r.title })) },
    });
    const c3 = setJsonLd("report-hub-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Reports", item: `${SITE_URL}/reports` },
        { "@type": "ListItem", position: 2, name, item: `${SITE_URL}${base}` }] });
    return () => { c1(); c2(); c3(); };
  }, [data, page, base, kind, name, head]);

  const c = dm
    ? { bg: "bg-[#0c1011]", text: "text-white", sub: "text-slate-400", muted: "text-slate-500", chip: "bg-[#1a2122] text-slate-300 hover:bg-[#222b2c]", btn: "bg-[#1a2122] text-slate-300 hover:bg-[#222b2c]", accent: "text-cyan-400" }
    : { bg: "bg-[#f8f9fa]", text: "text-[#191c1d]", sub: "text-[#3b494c]", muted: "text-[#6b7a7d]", chip: "bg-white text-[#3b494c] border border-black/10 hover:bg-black/5", btn: "bg-white border border-black/10 text-[#3b494c] hover:bg-black/5", accent: "text-[#006874]" };
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className={`min-h-screen ${c.bg}`}>
      <PublicPageNav active="reports" />
      <main className="max-w-6xl mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-16" data-testid={`report-hub-${kind}`}>
        <Link to="/reports" className={`inline-flex items-center gap-1 text-sm mb-4 ${c.muted} hover:underline`} data-testid="report-hub-back">
          <ArrowLeft className="w-3 h-3" /> All reports
        </Link>
        {notFound ? (
          <p className={`py-20 text-center ${c.muted}`} data-testid="report-hub-not-found">
            {kind === "category" ? "Category not found." : "No hub for this organisation (hubs exist for publishers with 3+ reports)."}
          </p>
        ) : !data ? (
          <div className="flex justify-center py-20"><RefreshCw className={`w-6 h-6 animate-spin ${c.muted}`} /></div>
        ) : (
          <>
            <div className="max-w-3xl mb-8">
              <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${c.accent}`}>{kind === "category" ? "Reports by category" : "Reports by publisher"}</p>
              <h1 className={`font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight ${c.text}`} data-testid="report-hub-title">{name}</h1>
              {kind === "category" ? (head.intro || "").split("\n\n").map((p, i) => (
                <p key={i} className={`text-sm md:text-base mb-3 ${c.sub}`} data-testid={`report-hub-intro-${i}`}>{p}</p>
              )) : (
                <p className={`text-sm md:text-base mb-3 ${c.sub}`} data-testid="report-hub-intro-0">
                  {data.total} reports published by {name}{head.focus ? ` — ${head.focus}` : ""}{head.area ? ` (${head.area})` : ""}. Each one is summarised in an original TourismIntel brief with key findings, numbers as published, methodology and the official link.
                  {head.homepage_url && <> Publisher site: <a href={head.homepage_url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 underline ${c.accent}`}>{head.homepage_url.replace(/^https?:\/\//, "").replace(/\/$/, "")} <ExternalLink className="w-3 h-3" /></a></>}
                </p>
              )}
              <p className={`text-xs ${c.muted}`}><FileText className="inline w-3 h-3 mr-1" />{data.total} reports · sorted by publication date</p>
            </div>

            {kind === "category" && data.other_categories?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1" data-testid="report-hub-other-categories">
                {data.other_categories.map((o) => (
                  <Link key={o.slug} to={o.url} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${c.chip}`} data-testid={`report-hub-chip-${o.slug}`}>
                    {o.label} <span className="opacity-60">({o.count})</span>
                  </Link>
                ))}
              </div>
            )}

            {data.reports.length === 0 ? (
              <p className={`py-16 text-center ${c.muted}`} data-testid="report-hub-empty">No reports on this page — <Link to={base} className="underline">back to the first page</Link>.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="report-hub-grid">
                {data.reports.map((r) => <ReportCard key={r.id} report={r} darkMode={dm} />)}
              </div>
            )}

            {data.total > PAGE_SIZE && (
              <nav className="mt-10 flex justify-center items-center gap-2" data-testid="report-hub-pagination">
                {page > 1 ? <Link to={page > 2 ? `${base}?page=${page - 1}` : base} className={`px-4 py-2 rounded-lg text-sm font-semibold ${c.btn}`} data-testid="report-hub-prev"><span className="inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Newer</span></Link>
                  : <span className={`px-4 py-2 rounded-lg text-sm font-semibold opacity-40 ${c.btn}`}>Newer</span>}
                <span className={`px-4 py-2 text-sm ${c.sub}`}>Page {page} of {totalPages}</span>
                {data.has_more ? <Link to={`${base}?page=${page + 1}`} className={`px-4 py-2 rounded-lg text-sm font-semibold ${c.btn}`} data-testid="report-hub-next"><span className="inline-flex items-center gap-1">Older <ArrowRight className="w-3.5 h-3.5" /></span></Link>
                  : <span className={`px-4 py-2 rounded-lg text-sm font-semibold opacity-40 ${c.btn}`}>Older</span>}
              </nav>
            )}
            <div className="mt-10"><ReportsSignupCta dm={dm} testid="report-hub-signup-cta" /></div>
          </>
        )}
      </main>
      <PublicPageFooter dark={dm} />
    </div>
  );
}
